import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { ModuleLayout } from "../../../layouts";
import { ContentCard, Button, FormField, Checkbox, Loader } from "../../../components";
import { ConfirmModal } from "../components";
import { IoChevronDown } from "react-icons/io5";
import { FiX } from "react-icons/fi";
import {
  getModulesApi,
  createPermissionApi,
  getPermissionByIdApi,
  updatePermissionApi,
} from "../../../DAL";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../../constants";

// ACCESSIBILITY_OPTIONS will be defined inside component with t()

// ── Multi-select Accessibility dropdown ───────────────────────────────────────
const AccessibilitySelect = ({ value = [], onChange, error, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggle = (optValue) => {
    onChange(
      value.includes(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value, optValue]
    );
  };

  const remove = (optValue) => onChange(value.filter((v) => v !== optValue));

  const borderCls = error
    ? "border-red-400"
    : open
    ? "border-primary-500"
    : "border-[#ccc]";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full border rounded-md px-3 py-2 text-sm bg-white text-left flex items-center justify-between focus:outline-none transition ${borderCls}`}
      >
        <span className="text-[#c0c0c0]">Select Accessibility</span>
        <IoChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && options && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#ccc] rounded-md shadow-md py-1">
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => toggle(opt.value)}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer select-none text-sm text-[#333]"
            >
              <Checkbox
                checked={value.includes(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              {opt.label}
            </div>
          ))}
        </div>
      )}

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((v) => {
            const label =
              options?.find((o) => o.value === v)?.label ?? v;
            return (
              <span
                key={v}
                className="inline-flex items-center gap-1 bg-primary-500 text-white text-xs font-medium px-2.5 py-1 rounded-full"
              >
                {label}
                <button
                  type="button"
                  onClick={() => remove(v)}
                  className="hover:opacity-75 cursor-pointer"
                >
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const PermissionForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const isEdit = location.pathname === ROUTES_CONSTANTS.UPDATE_PERMISSION;
  const permissionId = location.state?.id;

  // Redirect to list if edit mode was hit directly without an ID
  useEffect(() => {
    if (isEdit && !permissionId) {
      navigate(ROUTES_CONSTANTS.PERMISSIONS, { replace: true });
    }
  }, [isEdit, permissionId, navigate]);

  const [confirmData, setConfirmData] = useState(null);

  const ACCESSIBILITY_OPTIONS = [
    { value: "view", label: t('permissions.accessibilityView') },
    { value: "add", label: t('permissions.accessibilityAdd') },
    { value: "update", label: t('permissions.accessibilityUpdate') },
    { value: "delete", label: t('permissions.accessibilityDelete') },
  ];

  const methods = useForm({
    defaultValues: {
      permissionName: "",
      module: "",
      feature: "",
      accessibility: [],
    },
  });

  const { control, formState: { errors }, watch, setValue, reset } = methods;
  const watchedModule = watch("module");

  // ── Modules ─────────────────────────────────────────────────────────────────
  const { data: modulesData } = useQuery({
    queryKey: [QUERY_CONSTANTS.MODULES],
    queryFn: getModulesApi,
  });
  const modules = modulesData?.data?.modules || [];
  const moduleOptions = modules.map((m) => ({ value: m.name, label: m.name }));

  // ── Features (derived from selected module's embedded features array) ─────────
  const selectedModule = modules.find((m) => m.name === watchedModule);
  const featureOptions = (selectedModule?.features ?? []).map((f) => ({
    value: f.name,
    label: f.name,
  }));

  // ── Fetch permission for edit mode ──────────────────────────────────────────
  const { data: permissionData, isLoading: isLoadingPermission } = useQuery({
    queryKey: [QUERY_CONSTANTS.PERMISSION, permissionId],
    queryFn: async () => {
      const res = await getPermissionByIdApi(permissionId);
      return res.data;
    },
    enabled: isEdit && !!permissionId,
  });

  // Pre-fill form when permission data is ready
  useEffect(() => {
    if (!isEdit || !permissionData) return;
    reset({
      permissionName: permissionData.display_name || permissionData.name || "",
      module: permissionData.module || "",
      feature: permissionData.feature || "",
      accessibility: permissionData.accessibility || [],
    });
  }, [isEdit, permissionData, reset]);

  // ── Mutation ─────────────────────────────────────────────────────────────────
  const { mutate: submitPermission, isPending } = useMutation({
    mutationFn: (data) => {
      const payload = {
        name: data.permissionName,
        display_name: data.permissionName,
        module: data.module,
        feature: data.feature,
        accessibility: data.accessibility,
      };
      return isEdit
        ? updatePermissionApi(permissionId, payload)
        : createPermissionApi(payload);
    },
    onSuccess: (response) => {
      enqueueSnackbar(response?.message, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: [QUERY_CONSTANTS.PERMISSIONS] });
      navigate(ROUTES_CONSTANTS.PERMISSIONS);
    },
  });

  const onSubmit = (data) => setConfirmData(data);

  const handleConfirm = () => {
    submitPermission(confirmData);
    setConfirmData(null);
  };

  if (isEdit && isLoadingPermission) return <Loader />;

  return (
    <ModuleLayout
      title="Company Permissions"
      onBack={() => navigate(ROUTES_CONSTANTS.PERMISSIONS)}
    >
      <ContentCard className="h-full">
        <h2 className="text-2xl font-medium text-black mb-1">
          {isEdit ? t('permissions.updateTitle') : t('permissions.createTitle')}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {isEdit
            ? t('permissions.updateSubtitle')
            : t('permissions.createSubtitle')}
        </p>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <div className="mb-4">
              <FormField
                name="permissionName"
                label={t('permissions.name')}
                placeholder={t('permissions.namePlaceholder')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                name="module"
                type="select"
                label={t('permissions.module')}
                placeholder={t('permissions.modulePlaceholder')}
                options={moduleOptions}
                onChange={() => setValue("feature", "")}
              />
              <FormField
                name="feature"
                type="select"
                label={t('permissions.feature')}
                placeholder={t('permissions.featurePlaceholder')}
                options={featureOptions}
              />
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#666]">
                  {t('permissions.accessibility')}
                </label>
                <Controller
                  control={control}
                  name="accessibility"
                  render={({ field }) => (
                    <AccessibilitySelect
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.accessibility?.message}
                      options={ACCESSIBILITY_OPTIONS}
                    />
                  )}
                />
              </div>
            </div>
          </form>
        </FormProvider>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="secondary"
            size="lg"
            onClick={() => navigate(ROUTES_CONSTANTS.PERMISSIONS)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            size="lg"
            loading={isPending}
            onClick={methods.handleSubmit(onSubmit)}
          >
            {isEdit ? t('permissions.btnUpdate') : t('permissions.btnCreate')}
          </Button>
        </div>
      </ContentCard>

      {confirmData && (
        <ConfirmModal
          title={isEdit ? t('permissions.confirmUpdateTitle') : t('permissions.confirmCreateTitle')}
          confirmLabel={isEdit ? t('permissions.btnUpdate') : t('permissions.btnCreate')}
          onCancel={() => setConfirmData(null)}
          onConfirm={handleConfirm}
          loading={isPending}
        >
          <p>
            {isEdit
              ? t('permissions.confirmUpdateText')
              : t('permissions.confirmCreateText')}
          </p>
          <div className="space-y-1 text-primary-500">
            <p>{t('permissions.confirmNameLabel')}: [{confirmData.permissionName}]</p>
            <p>{t('permissions.confirmModuleLabel')}: [{confirmData.module}]</p>
            <p>{t('permissions.confirmFeatureLabel')}: [{confirmData.feature}]</p>
            <p>{t('permissions.confirmAccessibilityLabel')}: [{confirmData.accessibility.join(", ")}]</p>
          </div>
          <p className="mt-2">
            {t('permissions.confirmWarning')}
          </p>
          <p className="mt-2">{t('common.confirmProceed')}</p>
        </ConfirmModal>
      )}
    </ModuleLayout>
  );
};

export default PermissionForm;
